import React, { useState, useEffect, useRef } from 'react';
import { FaWeightScale } from 'react-icons/fa6';
import { FaChevronLeft, FaChevronDown } from 'react-icons/fa';
import { useResusContext } from '../Resus/ResusContext';
import { useCPRLog } from './CPRLog';
import { saveCurrentState, loadCurrentState } from './CprState/storage';
import { CPRState } from './CprState/types';
import SearchableDropdown from '../components/SearchableDropdown';
import ProtocolsDialog from './ProtocolsDialog';
import { cprEventEmitter, EVENTS } from './cprEvents';
import './OpeningConditions.scss';
import { getHospitalData } from './PerHospitalResusConfig';
import { formatAge as formatAgeUtil } from './CprManager/utils'; // Import the utility function
import { useTranslation } from 'react-i18next';

interface AgeOption {
  label: string;
  value: string;
}

interface OpeningConditionsProps {
  onUpdate?: () => void; 
  isNursingView: boolean;
}

interface ExtendedData {
  airwayType: 'טובוס' | 'טובוס עם בלונית' | 'LMA' | null;
  airwaySize: string;
  airwayDepth: string;
  hasIV: boolean;
  hasIO: boolean;
  additionalNotes: string;
}

const initialExtendedData: ExtendedData = {
  airwayType: null,
  airwaySize: '',
  airwayDepth: '',
  hasIV: false,
  hasIO: false,
  additionalNotes: ''
};

const OpeningConditions: React.FC<OpeningConditionsProps> = ({
  onUpdate,
  isNursingView
}) => {
  const { age, weight, updateContext, resetContext, protocols, setProtocols, hospital } = useResusContext();
  const { addEntry } = useCPRLog();
  const { t } = useTranslation(['resus']);
  
  const [localAge, setLocalAge] = useState(age);
  const [localWeight, setLocalWeight] = useState(weight !== null ? weight.toString() : '');
  const [estimatedMaleWeight, setEstimatedMaleWeight] = useState<string>('');
  const [estimatedFemaleWeight, setEstimatedFemaleWeight] = useState<string>('');
  const [agesForDropDown, setAgesForDropDown] = useState<AgeOption[]>([]);
  const [estimatedWeightByAge, setEstimatedWeightByAge] = useState<Record<string, { male: string; female: string }>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isAdditionalDataExpanded, setIsAdditionalDataExpanded] = useState(false);
  const [isProtocolsDialogOpen, setIsProtocolsDialogOpen] = useState(false);
  const [extendedData, setExtendedData] = useState<ExtendedData>(initialExtendedData);
  const [previousValues, setPreviousValues] = useState<ExtendedData>({
    airwayType: null,
    airwaySize: '',
    airwayDepth: '',
    hasIV: false,
    hasIO: false,
    additionalNotes: ''
  });
  // Added state for weight warning
  const [showWeightWarning, setShowWeightWarning] = useState(false);
  const weightWarningTimerRef = useRef<NodeJS.Timeout | null>(null);

  const hospitalData = getHospitalData(hospital);
  const airwaysDataFile = hospitalData.airways;
  const emergencyProtocols = hospitalData.protocols;

  useEffect(() => {
    const savedState = loadCurrentState();
    if (savedState?.openingConditions) {
      const {
        additionalNotes,
        airway,
        hasIV,
        hasIO
      } = savedState.openingConditions;
  
      setLocalAge(age);
      setLocalWeight(weight !== null ? weight.toString() : '');
      
      setExtendedData({
        airwayType: airway.type,
        airwaySize: airway.size,
        airwayDepth: airway.depth,
        hasIV,
        hasIO,
        additionalNotes
      });
  
      setPreviousValues({
        airwayType: airway.type,
        airwaySize: airway.size,
        airwayDepth: airway.depth,
        hasIV,
        hasIO,
        additionalNotes
      });
    }
  }, []);

  useEffect(() => {
    const fetchData = () => {
      try {
        parseRawDataToEstimatedWeights(airwaysDataFile);
        createDropDownData(airwaysDataFile);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setLocalAge(age);
    setLocalWeight(weight !== null ? weight.toString() : '');
  }, [age, weight]);

  useEffect(() => {
    if ((localAge == 0 || localAge) && estimatedWeightByAge[localAge]) {
      setEstimatedMaleWeight(estimatedWeightByAge[localAge].male);
      setEstimatedFemaleWeight(estimatedWeightByAge[localAge].female);
    } else {
      setEstimatedMaleWeight('');
      setEstimatedFemaleWeight('');
    }
  }, [localAge, estimatedWeightByAge]);

  // Effect to check for weight warnings when weight or age changes
  useEffect(() => {
    // Clear any existing timer
    if (weightWarningTimerRef.current) {
      clearTimeout(weightWarningTimerRef.current);
      weightWarningTimerRef.current = null;
    }

    // Check if we need to schedule or show a warning
    if (localAge && localWeight && estimatedFemaleWeight && estimatedMaleWeight) {
      const weightNum = Number(localWeight);
      const femaleWeightNum = Number(estimatedFemaleWeight);
      const maleWeightNum = Number(estimatedMaleWeight);
      
      // Use the larger of the two estimated weights for comparison
      const largerEstimatedWeight = Math.max(femaleWeightNum, maleWeightNum);
      const lowerEstimatedWeight = Math.min(femaleWeightNum, maleWeightNum);
      
      // If weight is >= 30% above the larger estimated weight, show warning immediately
      if (weightNum >= largerEstimatedWeight * 1.3) {
        setShowWeightWarning(true);
      } 
      // If weight is <= 30% below the smaller estimated weight, and
      // weight is >= 10 (i.e. the user entered two digits, meaning he
      // is not in the middle of the typing) - show warning immediately.
      else if (weightNum <= lowerEstimatedWeight * 0.7 && weightNum >= 10) {
        setShowWeightWarning(true);
      }
      // If weight is <= 30% below the smaller estimated weight,
      // schedule a delayed warning. we make the schedule because the user
      // may be in the middle of the typing.
      else if (weightNum <= lowerEstimatedWeight * 0.7) {
        weightWarningTimerRef.current = setTimeout(() => {
          setShowWeightWarning(true);
        }, 2000);
      } 
      // If weight is within normal range, hide any existing warning
      else {
        setShowWeightWarning(false);
      }
    } else {
      // If we don't have all needed data, hide any warning
      setShowWeightWarning(false);
    }

    // Cleanup timer when component unmounts
    return () => {
      if (weightWarningTimerRef.current) {
        clearTimeout(weightWarningTimerRef.current);
      }
    };
  }, [localWeight, localAge, estimatedFemaleWeight, estimatedMaleWeight]);

  const getMandatoryFields = () => {
    return { weight: true, age: true, protocol: false };    
  };

  const mandatoryFields = getMandatoryFields();

  const isFieldInvalid = (field: 'weight' | 'age') => {
    if (!mandatoryFields[field]) return false;
    if (!hasAttemptedSubmit) return false;

    switch (field) {
      case 'weight': return !localWeight;
      case 'age': return localAge == null;
      default: return false;
    }
  };

  const parseRawDataToEstimatedWeights = (data: any) => {
    const weightByAge: Record<string, { male: string; female: string }> = {};
    data.dataByAge.forEach(({ age, estimatedMaleWeight, estimatedFemaleWeight }: any) => {
      weightByAge[age] = { male: estimatedMaleWeight, female: estimatedFemaleWeight };
    });
    setEstimatedWeightByAge(weightByAge);
  };

  const createDropDownData = (data: any) => {
    const ages = data.dataByAge.map((item: any) => ({
      label: formatAgeUtil(item.age, t), // Use utility function
      value: item.age
    }));
    setAgesForDropDown(ages);
  };

  // Removed local formatAge function

  const applyMaleWeightRounded = () => setLocalWeight(Math.round(Number(estimatedMaleWeight)).toString());
  const applyFemaleWeightRounded = () => setLocalWeight(Math.round(Number(estimatedFemaleWeight)).toString());

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(',', '.');
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setLocalWeight(value);
    }
  };

  // Handle weight field blur event
  const handleWeightBlur = () => {
    if (localAge && localWeight && estimatedFemaleWeight && estimatedMaleWeight) {
      const weightNum = Number(localWeight);
      const femaleWeightNum = Number(estimatedFemaleWeight);
      const maleWeightNum = Number(estimatedMaleWeight);
      
      const lowerEstimatedWeight = Math.min(femaleWeightNum, maleWeightNum);
      
      // If weight is too low, show warning immediately on blur
      if (weightNum <= lowerEstimatedWeight * 0.7) {
        setShowWeightWarning(true);
      }
    }
  };

  const handleAirwayTypeChange = (type: ExtendedData['airwayType']) => {
    setExtendedData(prev => ({
      ...prev,
      airwayType: type,
      airwaySize: '',
      airwayDepth: ''
    }));
  };

  const getProtocolName = (protocolId: string): string | undefined => {
    for (const section of emergencyProtocols.emergencyProtocols) {
      const protocol = section.protocols.find(p => p.id === protocolId);
      if (protocol) {
        return protocol.name;
      }
    }
    return undefined;
  };

  const handleProtocolsUpdate = (newProtocols: string[]) => {
    const addedProtocols = newProtocols.filter(p => !protocols.includes(p));
    const removedProtocols = protocols.filter(p => !newProtocols.includes(p));
    
    if (addedProtocols.length > 0 || removedProtocols.length > 0) {
      const logParts: string[] = [];
      
      if (addedProtocols.length > 0) {
        const addedNames = addedProtocols
          .map(getProtocolName)
          .filter((name): name is string => name !== undefined);
          
        if (addedNames.length === 1) {
          logParts.push(t('protocols.addedSingle', { name: addedNames[0] }));
        } else if (addedNames.length > 1) {
          logParts.push(t('protocols.addedMultiple', { names: addedNames.join(', ') }));
        }
      }
      
      if (removedProtocols.length > 0) {
        const removedNames = removedProtocols
          .map(getProtocolName)
          .filter((name): name is string => name !== undefined);
          
        if (removedNames.length === 1) {
          logParts.push(t('protocols.removedSingle', { name: removedNames[0] }));
        } else if (removedNames.length > 1) {
          logParts.push(t('protocols.removedMultiple', { names: removedNames.join(', ') }));
        }
      }

      if (logParts.length > 0) {
        addEntry({
          timestamp: new Date().toISOString(),
          text: logParts.join('. '),
          type: 'openingConditions',
          isImportant: false
        });
      }
    }

    setProtocols(newProtocols);
    setIsProtocolsDialogOpen(false);
    saveCurrentState({
      resusContext: {
        protocols: newProtocols
      }
    }, 'resusContext');
  };

  const handleUpdate = () => {
    setHasAttemptedSubmit(true);
    
    const isMissingMandatoryFields = 
      (mandatoryFields.weight && !localWeight) ||
      (mandatoryFields.age && localAge == null) ||
      (mandatoryFields.protocol && protocols.length === 0);
  
    if (isMissingMandatoryFields) {
      return;
    }
    
    const weightValue = localWeight ? parseFloat(localWeight) : null;
    const timestamp = new Date().toISOString();
    
    React.startTransition(() => {
      updateContext(localAge, weightValue, protocols);
    });
    
    const stateUpdates: Partial<CPRState> = {
      openingConditions: {
        additionalNotes: extendedData.additionalNotes,
        airway: {
          type: extendedData.airwayType,
          size: extendedData.airwaySize,
          depth: extendedData.airwayDepth
        },
        hasIV: extendedData.hasIV,
        hasIO: extendedData.hasIO
      },
      
      resusContext: {
        age: localAge,
        weight: weightValue,
        protocols
      }
    };

    saveCurrentState(stateUpdates);

    if (localAge !== age || weightValue !== weight) {
      addEntry({
        timestamp,
        text: t('patientInfo.logEntry', { age: formatAgeUtil(localAge, t), weight: weightValue }), // Use utility function
        type: 'openingConditions',
        isImportant: false
      });
    }

    if (extendedData.airwayType && 
        (extendedData.airwayType !== previousValues.airwayType ||
          extendedData.airwaySize !== previousValues.airwaySize ||
          extendedData.airwayDepth !== previousValues.airwayDepth)) {
      let airwayText = t('airway.logEntry', { type: extendedData.airwayType });
      if (extendedData.airwaySize) {
        airwayText += t('airway.sizeEntry', { size: extendedData.airwaySize });
      }
      if ((extendedData.airwayType === 'טובוס' || extendedData.airwayType === 'טובוס עם בלונית') && extendedData.airwayDepth) {
        airwayText += t('airway.depthEntry', { depth: extendedData.airwayDepth });
      }
      addEntry({
        timestamp,
        text: airwayText,
        type: 'openingConditions',
        isImportant: false
      });
    }

    if ((extendedData.hasIV !== previousValues.hasIV) || 
        (extendedData.hasIO !== previousValues.hasIO)) {
      if (extendedData.hasIV || extendedData.hasIO) {
        const lines: string[] = [];
        if (extendedData.hasIV) lines.push('IV');
        if (extendedData.hasIO) lines.push('IO');
        addEntry({
          timestamp,
          text: t('line.logEntry', { lines: lines.join(', ') }),
          type: 'openingConditions',
          isImportant: false
        });
      }
    }

    if (extendedData.additionalNotes.trim() !== previousValues.additionalNotes.trim()) {
      if (extendedData.additionalNotes.trim()) {
        addEntry({
          timestamp,
          text: t('additionalNotes.logEntry', { notes: extendedData.additionalNotes }),
          type: 'openingConditions',
          isImportant: false
        });
      }
    }

    setPreviousValues
    ({
      airwayType: extendedData.airwayType,
      airwaySize: extendedData.airwaySize,
      airwayDepth: extendedData.airwayDepth,
      hasIV: extendedData.hasIV,
      hasIO: extendedData.hasIO,
      additionalNotes: extendedData.additionalNotes.trim()
    });
      
    setHasAttemptedSubmit(false);
    onUpdate?.();
  };

  const clearFields = () => {
    setLocalAge(null);
    setLocalWeight('');
    setExtendedData(initialExtendedData);
    setHasAttemptedSubmit(false);
    setShowWeightWarning(false);
  }

  const handleReset = () => {
    resetContext();
    setExtendedData(initialExtendedData);
    setHasAttemptedSubmit(false);
    setLocalAge(null);
    setLocalWeight('');
    setShowWeightWarning(false);
    setPreviousValues({
      airwayType: null,
      airwaySize: '',
      airwayDepth: '',
      hasIV: false,
      hasIO: false,
      additionalNotes: ''
    });
    saveCurrentState({
      openingConditions: {
        additionalNotes: '',
        hasIO: false,
        hasIV: false,
        airway: {
          type: null,
          size: '',
          depth: ''
        }
      }
    });
  };

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, handleReset);
    return () => unsubscribe();
  }, []);

  return (
    <div className="opening-conditions-container">
      <form>
        <div style={{ textAlign: 'right' }}>          
          <div className="row form-group">
            <div className="col-auto fs-5 col-auto-text-cols">
              {t('protocols.title')}
            </div>
            <div className="col input-col">
              <button
                type="button"
                className="btn gray-btn"
                style={{width: '100%'}}
                data-testid="openinig-conditions-protocols-chooser"
                onClick={() => setIsProtocolsDialogOpen(true)}
              >
              {protocols.length === 0 
                  ? t('protocols.select')
                  : protocols.length === 1 
                    ? getProtocolName(protocols[0])
                    : t('protocols.countSelected', { count: protocols.length })
              }
              </button>
            </div>
          </div>
          
          <div className="row form-group">
            <div className="col-auto fs-5 col-auto-text-cols">
              {t('patientInfo.age')}
            </div>
            <div className="col input-col">
              <SearchableDropdown
                options={agesForDropDown}
                value={localAge}
                onChange={(value) => setLocalAge(Number(value))}
                placeholder={t('patientInfo.selectAge')}
                isInvalid={isFieldInvalid('age')}
              />
            </div>
          </div>

          {localAge != null && (
            <div className="row form-group">              
              <div className="weight-estimate">
                <FaWeightScale className="weight-icon" />
                {t('weight.estimatedMale', { weight: estimatedMaleWeight })}
                <button 
                  type="button" 
                  className="btn btn-link ng-binding weight-button"
                  onClick={applyMaleWeightRounded}
                >
                  <span style={{fontWeight:"normal", marginRight:"3px"}}>
                  {t('weight.enterRounded')}
                  </span>
                </button>
            </div>
              <div className="weight-estimate">
                <FaWeightScale className="weight-icon" />
                {t('weight.estimatedFemale', { weight: estimatedFemaleWeight })}
                <button 
                  type="button" 
                  className="btn btn-link ng-binding weight-button"
                  onClick={applyFemaleWeightRounded}
                >
                  <span style={{fontWeight:"normal", marginRight:"3px"}}>
                  {t('weight.enterRounded')}
                  </span>
                </button>
              </div>            
            </div>
          )}

          <div className="row form-group">
            <div className="col-auto fs-5 col-auto-text-cols">
              {t('weight.labelWithUnit')}
            </div>
            <div className="col input-col">
              <input
                type="text"
                inputMode='decimal'
                pattern="[0-9]*[.,]?[0-9]*"
                maxLength={7}
                style={{ maxWidth: '230px' }}
                className={`form-control ${isFieldInvalid('weight') ? 'border-danger' : ''}`}
                id="weight"
                value={localWeight}
                onFocus={(e) => e.target.select()}
                onChange={handleWeightChange}
                onBlur={handleWeightBlur}
                data-testid="opening-conditions-weight"
              />
            </div>
          </div>

          {showWeightWarning && (
            <div className="row form-group">
              <span style={{ color: 'red' }}><b>{t('weight.warningDeviation')}</b></span>
            </div>
          )}

          {!isNursingView && (
            <div className="row form-group">
              <div className="collapsible-header" onClick={() => setIsAdditionalDataExpanded(!isAdditionalDataExpanded)}>
                {isAdditionalDataExpanded ? <FaChevronDown /> : <FaChevronLeft />}
                <span className="additional-data-header">{t('additionalData.title')}</span>
              </div>
              
              {isAdditionalDataExpanded && (
                <div className="opening-conditions">
                  <div className="input-group full-width">
                    <label className="bold-label">{t('airway.title')}:</label>
                    <div className="radio-options">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="airwayType"
                          value="טובוס עם בלונית"
                          checked={extendedData.airwayType === 'טובוס עם בלונית'}
                          onChange={(e) => handleAirwayTypeChange(e.target.value as 'טובוס עם בלונית')}
                        />
                        {t('airway.tubeWithBalloon')}
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="airwayType"
                          value="טובוס"
                          checked={extendedData.airwayType === 'טובוס'}
                          onChange={(e) => handleAirwayTypeChange(e.target.value as 'טובוס')}
                        />
                        {t('airway.tubeOnly')}
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="airwayType"
                          value="LMA"
                          checked={extendedData.airwayType === 'LMA'}
                          onChange={(e) => handleAirwayTypeChange(e.target.value as 'LMA')}
                        />
                        LMA
                      </label>
                    </div>
                  </div>

                  {extendedData.airwayType && (
                    <div className="input-group full-width">
                      <div style={{ display: 'flex', marginRight: '10px' }}>
                        <div className="input-group narrow">
                          <label>{t('airway.size')}:</label>
                          <input
                            type="number"
                            style={{backgroundColor: 'white', borderRadius: '8px'}}
                            inputMode='decimal'
                            data-testid="opening-conditions-size-input"
                            value={extendedData.airwaySize}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setExtendedData(prev => ({ ...prev, airwaySize: e.target.value }))}
                          />
                        </div>

                        {(extendedData.airwayType === 'טובוס' || extendedData.airwayType === 'טובוס עם בלונית') && (
                          <div className="input-group narrow">
                            <label>{t('airway.depth')}:</label>
                            <input
                              type="number"
                              inputMode='decimal'
                              style={{backgroundColor: 'white', borderRadius: '8px'}}
                              data-testid="opening-conditions-depth-input"
                              value={extendedData.airwayDepth}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => setExtendedData(prev => ({ ...prev, airwayDepth: e.target.value }))}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="input-group full-width">
                    <label className="bold-label">{t('line.title')}:</label>
                    <div className="radio-options">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={extendedData.hasIV}
                          onChange={(e) => setExtendedData(prev => ({ ...prev, hasIV: e.target.checked }))}
                        />
                        <span>IV</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={extendedData.hasIO}
                          onChange={(e) => setExtendedData(prev => ({ ...prev, hasIO: e.target.checked }))}
                        />
                        <span>IO</span>
                      </label>
                    </div>
                  </div>

                  <div className="input-group full-width">
                    <textarea
                      className="form-control notes-textarea"
                      rows={3}
                      value={extendedData.additionalNotes}
                      onChange={(e) => setExtendedData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                      placeholder={t('additionalNotes.placeholder')}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="row form-group">
            <div className="col text-center">
              <button
                type="button"
                className="btn btn-update"
                style={{ marginLeft: "15px" }}
                onClick={handleUpdate}
                data-testid="opening-conditions-confirm"
              >
                {t('buttons.update')}
              </button>
              <button
                type="button"
                className="btn btn-reset"
                data-testid="opening-conditions-clear"
                onClick={clearFields}
              >
                {t('buttons.clear')}
              </button>
            </div>
          </div>

          {hasAttemptedSubmit && (
            mandatoryFields.weight && !localWeight ||
            mandatoryFields.age && localAge == null ||
            mandatoryFields.protocol && protocols.length === 0
          ) && (
            <div className="row form-group">
              <div className="col text-center">
                <span style={{ color: 'red' }}>{t('validation.requiredFields')}</span>
              </div>
            </div>
          )}
        </div>
      </form>

      <ProtocolsDialog
        isOpen={isProtocolsDialogOpen}
        onClose={() => setIsProtocolsDialogOpen(false)}
        protocols={protocols}
        onUpdate={handleProtocolsUpdate}
      />
    </div>
  );
};

export default OpeningConditions;
